import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { getCountryObjs } from '@luxuryescapes/lib-countries';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { PinnedOffersConfig, deletePinnedOffersConfig } from '~/services/PublicOfferFeedService';

import dateFormatter from '~/utils/dateFormatter';

import PinnedOffersConfigForm from './PinnedOffersConfigForm';

const getCountryOptions = () => {
  const countries = getCountryObjs();
  return Object.values(countries)
    .map((country) => ({
      code: country.iso2,
      label: `${country.countryName} (${country.iso2})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const countryOptions = getCountryOptions();

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 0.5,
};

const getColumns = ({
  onEdit,
  onDelete,
}: {
  onEdit: (row: PinnedOffersConfig) => void;
  onDelete: (row: PinnedOffersConfig) => void;
}): Array<GridColDef> => [
  {
    field: 'user_regions',
    headerName: 'User Regions',
    ...sharedOpts,
    renderCell: (params) => <Typography>{params.row.user_regions?.join(', ')}</Typography>,
  },
  {
    field: 'offer_country',
    headerName: 'Offer Country',
    ...sharedOpts,
    renderCell: (params) => {
      const countryOption = countryOptions.find((option) => option.code === params.row.offer_country);
      return <Typography>{countryOption?.label || params.row.offer_country}</Typography>;
    },
  },
  {
    field: 'distance_lookup',
    headerName: 'Distance Lookup',
    ...sharedOpts,
    renderCell: (params) => {
      return <Typography>{params.row.distance_lookup}</Typography>;
    },
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    ...sharedOpts,
    renderCell: (params) => <Typography>{dateFormatter(params.row.created_at)}</Typography>,
  },
  {
    field: 'updated_at',
    headerName: 'Updated At',
    ...sharedOpts,
    renderCell: (params) => <Typography>{dateFormatter(params.row.updated_at)}</Typography>,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    ...sharedOpts,
    renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" size="small" onClick={() => onEdit(params.row)}>
          Edit
        </Button>
        <Button variant="outlined" size="small" onClick={() => onDelete(params.row)}>
          Delete
        </Button>
      </Stack>
    ),
  },
];

type Props = {
  selectedCountry: { code: string; label: string } | null;
  setSelectedCountry: (value: { code: string; label: string } | null) => void;
  distanceLookup: '10km' | '100km' | '1000km' | 'country_radius' | undefined;
  setDistanceLookup: (value: '10km' | '100km' | '1000km' | 'country_radius' | undefined) => void;
  data: Array<PinnedOffersConfig>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  fetchData: () => void;
};

export default function PinnedOffersConfigTab({
  selectedCountry,
  setSelectedCountry,
  distanceLookup,
  setDistanceLookup,
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  fetchData,
}: Props) {
  const [open, setOpen] = useState(false);
  const [editableRow, setEditableRow] = useState<PinnedOffersConfig | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<PinnedOffersConfig | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();

  const rows = useMemo(() => data.map((row, index) => ({ id: index, ...row })), [data]);

  const handleOpen = () => {
    setEditableRow(undefined);
    setOpen(true);
  };

  const handleEdit = (row: PinnedOffersConfig) => {
    setEditableRow(row);
    setOpen(true);
  };

  const handleDelete = (row: PinnedOffersConfig) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (rowToDelete?.id) {
      try {
        await deletePinnedOffersConfig(rowToDelete.id);
        fetchData();
        setDeleteDialogOpen(false);
        setRowToDelete(undefined);
      } catch (error) {
        console.error('Failed to delete pinned offers config:', error);
        enqueueSnackbar('Failed to delete pinned offers config', { variant: 'error' });
      }
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(undefined);
  };

  return (
    <>
      <Grid2 container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid2 size={8} container spacing={2}>
          <Grid2 size={6} container spacing={2}>
            <Autocomplete
              options={countryOptions}
              getOptionLabel={(option) => option.label}
              value={selectedCountry}
              onChange={(_, newValue) => setSelectedCountry(newValue)}
              renderInput={(params) => <TextField {...params} label="Country" fullWidth />}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              fullWidth
            />
          </Grid2>
          <Grid2 size={6} container spacing={2}>
            <Select
              id="distance_lookup"
              displayEmpty
              value={distanceLookup ?? ''}
              onChange={(e) => setDistanceLookup((e.target.value || undefined) as typeof distanceLookup)}
              fullWidth
            >
              <MenuItem value="">Distance Lookup</MenuItem>
              <MenuItem value="10km">10km</MenuItem>
              <MenuItem value="100km">100km</MenuItem>
              <MenuItem value="1000km">1000km</MenuItem>
              <MenuItem value="country_radius">Country Radius</MenuItem>
            </Select>
          </Grid2>
        </Grid2>
        <Grid2 size={4} container spacing={2}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleOpen}>
              New Pinned Offers Config
            </Button>
          </Stack>
        </Grid2>
      </Grid2>
      <PinnedOffersConfigForm open={open} setOpen={setOpen} fetchData={fetchData} editableRow={editableRow} />

      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the Pinned Offers configuration for Country: {rowToDelete?.id}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} variant="text">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={rows}
          columns={getColumns({ onEdit: handleEdit, onDelete: handleDelete })}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[20, 50, 100]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
        />
      </Box>
    </>
  );
}
